import { useEffect, useRef, useState } from 'react';
import type { GoldenGateEngine, Mode } from './engine';
import type { FlockSnapshot } from './flock/flock';
import { SplitFlap } from './split-flap';
import { wireJoystick, wireThrottle } from './touch';

type Kill = { key: number; killer: string; killerColor: string; victim: string; victimColor: string };

// A landing section: the Golden Gate in the Santiago hero's x-ray ink, with a condor gliding
// round it behind the copy and other pilots' condors flying through. "Vuela el cóndor" (or F)
// hands the condor to the visitor right there in the section — keyboard and wheel on a
// desktop, a joystick and a throttle lever on a phone — and the flock (FlockChannel) shows
// everyone flying it at the same time.
//
// The engine (three.js, the bridge, the socket) is imported only once the section comes near
// the viewport, so the rest of the landing never pays for it.

const PALETTE = ['#F5C542', '#4FD1C5', '#7AE582', '#B794F6', '#63B3ED', '#F687B3', '#FF8C42', '#FFFFFF'];

function flyingLabel(others: number) {
  if (others <= 0) return null;
  return others === 1 ? '1 cóndor volando ahora' : `${others} cóndores volando ahora`;
}

export function GoldenGateSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const fadeRef = useRef<HTMLDivElement>(null);
  const joyBaseRef = useRef<HTMLDivElement>(null);
  const joyKnobRef = useRef<HTMLDivElement>(null);
  const leverRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const leverKnobRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GoldenGateEngine | null>(null);

  const [near, setNear] = useState(false);
  const [inView, setInView] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<Mode>('ambient');
  const [flock, setFlock] = useState<FlockSnapshot | null>(null);
  const [touch, setTouch] = useState(false);
  const [editing, setEditing] = useState(false);
  const [swatches, setSwatches] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [shotDownBy, setShotDownBy] = useState<{ codename: string; color: string; key: number } | null>(null);
  const [kills, setKills] = useState<Kill[]>([]);

  // Load the engine once the section is within a screen of the viewport.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        io.disconnect();
      }
    }, { rootMargin: '100% 0px' });
    io.observe(section);
    const coarse = matchMedia('(pointer: coarse)');
    const onCoarse = () => setTouch(coarse.matches);
    onCoarse();
    coarse.addEventListener('change', onCoarse);
    return () => {
      io.disconnect();
      coarse.removeEventListener('change', onCoarse);
    };
  }, []);

  // Whether enough of the section is on screen for F to mean "fly" (and not hijack the key on
  // the rest of the landing).
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.35 });
    io.observe(section);
    return () => io.disconnect();
  }, []);

  // F takes off, like the button, while the section is in view.
  useEffect(() => {
    if (!inView || !ready || failed || mode !== 'ambient') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'KeyF' || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = document.activeElement as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      setMode('game');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inView, ready, failed, mode]);

  useEffect(() => {
    if (!near) return;
    const host = canvasHostRef.current;
    const section = sectionRef.current;
    const labels = labelsRef.current;
    const fade = fadeRef.current;
    if (!host || !section || !labels || !fade) return;
    let disposed = false;
    let cleanup = () => {};
    void import('./engine').then(({ GoldenGateEngine }) => {
      if (disposed) return;
      let engine: GoldenGateEngine;
      try {
        engine = new GoldenGateEngine(host, { labels, fade }, {
          onReady: () => setReady(true),
          onFlock: setFlock,
          onExit: () => setMode('ambient'),
          onCrash: () => {},
          onShotDown: (killer) => setShotDownBy({ ...killer, key: Date.now() }),
          onKills: (list) =>
            setKills((prev) =>
              [
                ...prev,
                ...list.map(([killer, killerColor, victim, victimColor], i) => ({ key: Date.now() + i, killer, killerColor, victim, victimColor })),
              ].slice(-4),
            ),
          onError: () => setFailed(true),
        });
      } catch {
        setFailed(true); // no WebGL
        return;
      }
      engineRef.current = engine;
      if (import.meta.env.DEV) (window as unknown as { __gg?: GoldenGateEngine }).__gg = engine;
      engine.start();
      void engine.load();
      const resize = new ResizeObserver(() => engine.resize());
      resize.observe(host);
      const visibility = new IntersectionObserver((entries) => {
        engine.setVisible(entries.some((e) => e.isIntersecting) && !document.hidden);
      });
      visibility.observe(section);
      const onHidden = () => engine.setVisible(!document.hidden);
      document.addEventListener('visibilitychange', onHidden);
      cleanup = () => {
        resize.disconnect();
        visibility.disconnect();
        document.removeEventListener('visibilitychange', onHidden);
        engine.dispose();
        engineRef.current = null;
      };
    });
    return () => {
      disposed = true;
      cleanup();
    };
  }, [near]);

  // Flying happens in place, inside the section, as on the tech-week landing: the page brings
  // the section to the middle of the screen and holds still while the condor flies (otherwise
  // the arrows, the space bar, the wheel and a thumb on a phone would move the page instead).
  useEffect(() => {
    engineRef.current?.setMode(mode);
    if (mode !== 'game') return;
    setEditing(false);
    setSwatches(false);
    const section = sectionRef.current;
    const html = document.documentElement;
    const body = document.body;
    const previous = { html: html.style.overflow, body: body.style.overflow, overscroll: html.style.overscrollBehavior };
    if (section) {
      const r = section.getBoundingClientRect();
      const top = window.scrollY + r.top - Math.max(0, (window.innerHeight - r.height) / 2);
      window.scrollTo({ top, behavior: Math.abs(r.top) > 4 ? 'smooth' : 'instant' });
    }
    // lock once the scroll has settled: a smooth scroll stops dead under overflow: hidden
    const lock = window.setTimeout(() => {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
    }, 450);
    return () => {
      window.clearTimeout(lock);
      html.style.overflow = previous.html;
      body.style.overflow = previous.body;
      html.style.overscrollBehavior = previous.overscroll;
    };
  }, [mode]);

  // The touch controls exist only while flying on a coarse pointer.
  useEffect(() => {
    const engine = engineRef.current;
    if (mode !== 'game' || !touch || !engine) return;
    const base = joyBaseRef.current;
    const knob = joyKnobRef.current;
    const lever = leverRef.current;
    const track = trackRef.current;
    const leverKnob = leverKnobRef.current;
    if (!base || !knob || !lever || !track || !leverKnob) return;
    const unJoy = wireJoystick(base, knob, engine.keys);
    const unLever = wireThrottle(lever, track, leverKnob, (v) => engine.setThrottle(v));
    return () => {
      unJoy();
      unLever();
    };
  }, [mode, touch]);

  useEffect(() => {
    if (!kills.length) return;
    const t = setTimeout(() => setKills((k) => k.slice(1)), 5000);
    return () => clearTimeout(t);
  }, [kills]);

  useEffect(() => {
    if (!shotDownBy) return;
    const t = setTimeout(() => setShotDownBy(null), 3000);
    return () => clearTimeout(t);
  }, [shotDownBy]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), message.error ? 4000 : 2500);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (flock?.pilot?.renamed) setMessage({ text: `tu nombre estaba en uso: ahora eres ${flock.pilot.codename}`, error: false });
  }, [flock?.pilot?.renamed, flock?.pilot?.codename]);

  const flying = mode === 'game';
  const pilot = flock?.pilot ?? null;
  const others = flying ? Math.max(0, (flock?.online ?? 0) - 1) : (flock?.online ?? 0);

  async function rename(value: string) {
    const engine = engineRef.current;
    const name = value.trim();
    setEditing(false);
    if (!engine || !name || name === pilot?.codename) return;
    try {
      const p = await engine.rename(name);
      setMessage({ text: `ahora eres ${p.codename}`, error: false });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'no se pudo cambiar', error: true });
    }
  }

  async function recolor(color: string) {
    const engine = engineRef.current;
    setSwatches(false);
    if (!engine) return;
    try {
      await engine.recolor(color);
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'no se pudo cambiar', error: true });
    }
  }

  return (
    <section
      ref={sectionRef}
      // never taller than the screen (a phone on its side is ~390 px tall): in flight the whole
      // section, controls included, has to be in view at once
      className="theme-ph26 selection-on-light relative h-[92svh] min-h-[min(560px,100svh)] w-full overflow-hidden bg-background text-primary"
      aria-label="San Francisco"
    >
      <div
        ref={stageRef}
        className="absolute inset-0"
        // behind the page a vertical swipe scrolls the page; in flight every touch is the pilot's
        style={{ touchAction: flying ? 'none' : 'pan-y', overscrollBehavior: flying ? 'none' : undefined }}
      >
        <div ref={canvasHostRef} className="absolute inset-0" />
        {/* the section's top edge in the page's own black, whatever the scene has up there (the
            hills' darker silhouettes reach it at the sides): the slide above runs straight in */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[22%] bg-gradient-to-b from-background via-background/60 to-transparent" />
        <div ref={labelsRef} className="pointer-events-none absolute inset-0 overflow-hidden" />
        <div ref={fadeRef} className="pointer-events-none absolute inset-0 bg-background opacity-0 transition-opacity duration-700" />

        {/* behind the page: the copy and the way in */}
        {!flying && (
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between bg-gradient-to-b from-background/80 via-transparent to-background/70 px-6 py-12 md:px-14 md:py-16">
            {/* the title sits in the night sky, above the horizon: the bridge below stays clear */}
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] opacity-70">
                destino · 37.8199° N 122.4783° W
              </p>
              <SplitFlap
                rows={['SAN', 'FRANCISCO']}
                label="San Francisco"
                // on a phone "FRANCISCO" (9 cells of 0.72em) has to fit the width inside the gutters
                className="mt-2 text-[min(12.6vw,5.6rem)] md:text-[clamp(4.5rem,8.6vw,8.5rem)]"
              />
              <p className="mt-4 max-w-2xl font-sans text-sm opacity-80 md:text-base">
                Al equipo ganador le pagamos los pasajes a San Francisco. Puede ser fácilmente el viaje más
                importante de toda tu carrera.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3">
              <button
                type="button"
                disabled={!ready || failed}
                onClick={() => setMode('game')}
                aria-keyshortcuts="F"
                className="pointer-events-auto inline-flex min-h-12 items-center justify-center gap-3 border-2 border-primary bg-primary px-8 py-2.5 font-title text-background text-base uppercase tracking-[0.12em] transition hover:scale-[1.02] disabled:cursor-wait disabled:opacity-50 md:min-h-14 md:text-xl"
              >
                {failed ? 'sin 3d en este navegador' : ready ? 'vuela el cóndor' : 'cargando el puente…'}
                {ready && !failed && !touch && (
                  <kbd className="inline-flex h-6 min-w-6 items-center justify-center border border-background/60 px-1.5 font-mono text-[11px] leading-none tracking-normal md:h-7 md:min-w-7 md:text-xs">
                    F
                  </kbd>
                )}
              </button>
              {flyingLabel(others) && (
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] opacity-80">{flyingLabel(others)}</p>
              )}
            </div>
          </div>
        )}

        {/* in flight */}
        {flying && (
          <>
            <button
              type="button"
              onClick={() => setMode('ambient')}
              className="absolute top-4 left-4 z-10 flex h-11 items-center gap-2 border border-primary/60 bg-background/70 px-4 font-mono text-xs uppercase tracking-[0.16em] backdrop-blur-sm transition hover:bg-primary hover:text-background"
              aria-label="Salir del vuelo"
            >
              <span aria-hidden>✕</span>
              <span className={touch ? 'hidden' : ''}>salir · esc</span>
            </button>

            <div
              className="absolute top-4 right-4 z-10 flex max-w-[calc(100vw-96px)] flex-col items-end gap-2 font-mono text-xs"
              style={{ ['--pilot' as string]: pilot?.color ?? '#ffffff' }}
            >
              <div className="flex h-11 min-w-0 items-center gap-2 border border-primary/60 bg-background/70 px-3 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => setSwatches((s) => !s)}
                  className="h-4 w-4 shrink-0 rounded-full border border-background"
                  style={{ background: 'var(--pilot)' }}
                  aria-label="Cambiar color"
                />
                {editing ? (
                  <input
                    // biome-ignore lint/a11y/noAutofocus: the field appears because the pilot asked to rename
                    autoFocus
                    defaultValue={pilot?.codename ?? ''}
                    maxLength={24}
                    spellCheck={false}
                    aria-label="Tu nombre"
                    className="w-40 min-w-0 bg-transparent text-primary outline-none"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') void rename(e.currentTarget.value);
                      if (e.key === 'Escape') setEditing(false);
                    }}
                    onBlur={(e) => void rename(e.currentTarget.value)}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => pilot && setEditing(true)}
                    title="Cambiar nombre"
                    className="min-w-0 truncate"
                    style={{ color: 'var(--pilot)' }}
                  >
                    {pilot?.codename ?? (flock?.status === 'offline' ? 'sin conexión' : 'conectando…')}
                  </button>
                )}
                <span className="shrink-0 opacity-70">
                  {others > 0 ? (touch ? `+${others}` : `+${others} volando`) : touch ? '' : 'vuelas solo'}
                </span>
              </div>
              {swatches && (
                <div className="flex gap-1.5 border border-primary/60 bg-background/80 p-2 backdrop-blur-sm">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => void recolor(c)}
                      className={`h-6 w-6 rounded-full border-2 ${pilot?.color === c ? 'border-primary' : 'border-transparent'}`}
                      style={{ background: c }}
                      aria-label={c}
                      aria-pressed={pilot?.color === c}
                    />
                  ))}
                </div>
              )}
              {message && (
                <p className={`max-w-xs bg-background/80 px-2 py-1 text-right ${message.error ? 'text-[#ff6b6b]' : ''}`}>{message.text}</p>
              )}
            </div>

            {/* a crash speaks for itself (the burst and the fade); only who shot you down is news */}
            {shotDownBy && (
              <p key={shotDownBy.key} className="pointer-events-none absolute inset-x-0 top-1/3 z-10 px-4 text-center font-title text-2xl uppercase tracking-[0.08em] md:text-3xl">
                te derribó <span style={{ color: shotDownBy.color }}>{shotDownBy.codename}</span>
              </p>
            )}

            {kills.length > 0 && (
              <ul className="pointer-events-none absolute top-20 left-4 z-10 flex flex-col gap-1 font-mono text-[11px]">
                {kills.map((k) => (
                  <li key={k.key} className="bg-background/70 px-2 py-0.5">
                    <span style={{ color: k.killerColor }}>{k.killer}</span> <span className="opacity-60">derribó a</span>{' '}
                    <span style={{ color: k.victimColor }}>{k.victim}</span>
                  </li>
                ))}
              </ul>
            )}

            {!touch && (
              <p className="pointer-events-none absolute inset-x-0 bottom-5 z-10 text-center font-mono text-[11px] uppercase tracking-[0.16em] opacity-70">
                a / d gira · w / s sube y baja · shift o rueda acelera · f o clic dispara · arrastra para mirar
              </p>
            )}

            {touch && (
              <button
                type="button"
                className="absolute right-5 bottom-14 z-10 flex h-20 w-20 select-none items-center justify-center rounded-full border-2 border-primary bg-primary/25 font-mono text-[10px] uppercase tracking-[0.18em] active:bg-primary active:text-background"
                style={{ touchAction: 'none' }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  engineRef.current?.setTrigger(true);
                }}
                onPointerUp={() => engineRef.current?.setTrigger(false)}
                onPointerCancel={() => engineRef.current?.setTrigger(false)}
                onContextMenu={(e) => e.preventDefault()}
              >
                fuego
              </button>
            )}

            {touch && (
              <>
                <div
                  ref={leverRef}
                  className="gg-throttle absolute bottom-10 left-5 z-10 flex flex-col items-center gap-2"
                  style={{ touchAction: 'none' }}
                >
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] opacity-70">vel</span>
                  <div ref={trackRef} className="gg-track relative h-[150px] w-11 rounded-full border border-primary/50 bg-background/60 max-[520px]:landscape:h-[104px]">
                    <div className="gg-fill absolute inset-x-0 bottom-0 rounded-full bg-primary/40" />
                    <div ref={leverKnobRef} className="absolute bottom-0 left-0 h-11 w-11 rounded-full border-2 border-primary bg-background" />
                  </div>
                </div>
                <div
                  ref={joyBaseRef}
                  className="absolute bottom-10 left-1/2 z-10 flex h-[104px] w-[104px] -translate-x-1/2 items-center justify-center rounded-full border border-primary/50 bg-background/50"
                  style={{ touchAction: 'none' }}
                  aria-label="Dirección"
                >
                  <div ref={joyKnobRef} className="h-[42px] w-[42px] rounded-full bg-primary/70" />
                </div>
              </>
            )}
          </>
        )}

        {/* the credits the bridge model (CC BY 4.0) and the terrain owe, kept off the clean UI */}
        <p className="sr-only">puente: «golden gate» de jcidmarti (cc by 4.0) · relieve: terrain tiles (aws)</p>
      </div>
    </section>
  );
}
